import React, { useState, useMemo } from 'react';
import { List, useTable, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { KnowledgeBaseDto, VisionEntityType } from '@wellverse/shared-ts';

export const KnowledgeBaseList: React.FC = () => {
  const { tableProps } = useTable<KnowledgeBaseDto>({
    resource: 'knowledge-bases',
    pagination: { current: 1, pageSize: 20 },
  });

  const [searchText, setSearchText] = useState('');

  const filteredData = useMemo(() => {
    if (!searchText) return tableProps.dataSource;
    const lower = searchText.toLowerCase();
    return tableProps.dataSource?.filter((item: any) =>
      item.name?.toLowerCase().includes(lower) ||
      item.description?.toLowerCase().includes(lower)
    );
  }, [tableProps.dataSource, searchText]);

  return (
    <List resource="knowledge-bases">
      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by name, description..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
          allowClear
        />
      </div>
      <Table {...tableProps} dataSource={filteredData} rowKey="id">
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column dataIndex="description" title="Description" render={(v) => v || '—'} />
        <Table.Column
          dataIndex="entityType"
          title="Entity Type"
          filters={Object.values(VisionEntityType).map((t) => ({ text: t, value: t }))}
          onFilter={(value, record: any) => record.entityType === value}
          render={(type: VisionEntityType) => (
            <Tag
              style={{
                borderColor: '#3ecf8e',
                color: '#3ecf8e',
                background: '#3ecf8e15',
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {type}
            </Tag>
          )}
        />
        <Table.Column dataIndex="entityCount" title="Entities" render={(v) => v ?? '—'} />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          filters={[
            { text: 'Active', value: true },
            { text: 'Inactive', value: false },
          ]}
          onFilter={(value, record: any) => record.isActive === value}
          render={(isActive: boolean) => (
            <Tag
              style={{
                borderColor: isActive ? '#3ecf8e' : '#898989',
                color: isActive ? '#3ecf8e' : '#898989',
                background: isActive ? '#3ecf8e15' : '#89898915',
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: KnowledgeBaseDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="knowledge-bases" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="knowledge-bases" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
